import { UR_Closer, UR_LoanOfficer, UR_LoanOfficerManager, UR_LoanProcessor, UR_Owner, UR_SuperAdmin, UR_Underwriter } from 'State'
import type {
  AssignmentRecord,
  AssignOrganizationsPayload,
  AssignOrganizationsResult,
  CreateOrgPayload,
  DisableUserPayload,
  EditUserPayload,
  InviteOrganizationPayload,
  InvitePayload,
  InviteResult,
  Member,
  MemberListFilters,
  MemberPage,
  Organization,
  OrganizationNameMatch,
  OrgRole,
} from '../types'
import { makeOrgShort, normalizeOrganizationPlan, pickOrgColor } from '../utils'

function getAuthToken(): string {
  return sessionStorage.getItem('ZeitroA') || ''
}

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json;charset=utf-8',
    Authorization: `Bearer ${getAuthToken()}`,
    Cache: 'no-cache',
  }
}

export async function searchOrganizationNames(query: string): Promise<OrganizationNameMatch[]> {
  const q = query.trim()
  if (q.length < 2) return []

  const response = await fetch(`/los/searchorganizationnames?q=${encodeURIComponent(q)}`, {
    method: 'GET',
    headers: authHeaders(),
  })
  if (!response.ok) {
    throw new Error('Failed to search organization names')
  }
  const data = await response.json()
  if (data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to search organization names')
  }
  return data.organizations || []
}

export async function checkOrganizationNameAvailable(organizationName: string): Promise<boolean> {
  const response = await fetch('/los/checkorganizationname', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ organizationName: organizationName.trim() }),
  })
  if (!response.ok) {
    throw new Error('Failed to check organization name')
  }
  const data = await response.json()
  if (data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to check organization name')
  }
  return !!data.Available
}

export interface CreateOrganizationResult {
  customerId: string
  companyName: string
  token: string
  plan: string
}

export async function createOrganization(payload: CreateOrgPayload): Promise<CreateOrganizationResult> {
  const response = await fetch('/los/createorganization', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to create organization')
  }
  return {
    customerId: data.CustomerID,
    companyName: data.CompanyName,
    token: data.Token,
    plan: normalizeOrganizationPlan(data.Plan),
  }
}

interface ManagedOrganizationApiRecord {
  customerId: string
  companyName: string
  address?: string
  plan?: string
  paidSeats?: number
  paidUsers?: number
  memberCount?: number
  isHardMoneyLoanCustomer?: boolean
  roles?: string[]
  canManageUsers?: boolean
  canCreateOrganization?: boolean
}

export interface ManagedOrganizationsResult {
  canCreateOrganization: boolean
  organizations: Organization[]
}

interface LoanOfficerApiRecord {
  ID?: string
  Email?: string
  FirstName?: string
  LastName?: string
  Phone?: string
  Nmls?: string
  Roles?: string[]
  Status?: string
  AccountTier?: string
  accounttier?: string
  accountTier?: string
  OrganizationIds?: string[]
}

function roleDisplayName(roles: string[] | undefined): OrgRole {
  if (!roles || roles.length === 0) return 'Loan Officer'
  if (roles.includes(UR_SuperAdmin)) return 'Super Admin'
  if (roles.includes(UR_Owner)) return 'Owner'
  if (roles.includes(UR_LoanOfficerManager)) return 'LO Manager'
  if (roles.includes(UR_Underwriter)) return 'Underwriter'
  if (roles.includes(UR_Closer)) return 'Closer'
  if (roles.includes(UR_LoanProcessor)) return 'Processor'
  if (roles.includes(UR_LoanOfficer)) return 'Loan Officer'
  return 'Loan Officer'
}

function roleDisplayNames(roles: string[] | undefined): OrgRole[] {
  if (!roles || roles.length === 0) return ['Loan Officer']

  const roleMap: { rawRole: string; displayRole: OrgRole }[] = [
    { rawRole: UR_SuperAdmin, displayRole: 'Super Admin' },
    { rawRole: UR_Owner, displayRole: 'Owner' },
    { rawRole: UR_LoanOfficerManager, displayRole: 'LO Manager' },
    { rawRole: UR_Underwriter, displayRole: 'Underwriter' },
    { rawRole: UR_Closer, displayRole: 'Closer' },
    { rawRole: UR_LoanProcessor, displayRole: 'Processor' },
    { rawRole: UR_LoanOfficer, displayRole: 'Loan Officer' },
  ]
  const displayRoles = roleMap
    .filter((role) => roles.includes(role.rawRole))
    .map((role) => role.displayRole)

  return displayRoles.length > 0 ? displayRoles : ['Loan Officer']
}

function rawRoleName(role: OrgRole): string {
  if (role === 'Super Admin') return UR_SuperAdmin
  if (role === 'Owner') return UR_Owner
  if (role === 'Admin' || role === 'LO Manager') return UR_LoanOfficerManager
  if (role === 'Underwriter') return UR_Underwriter
  if (role === 'Closer') return UR_Closer
  if (role === 'Processor') return UR_LoanProcessor
  return UR_LoanOfficer
}

function inviteOrganizationsFromRoles(roles: Record<string, OrgRole[]>): InviteOrganizationPayload[] {
  return Object.entries(roles).map(([customerId, orgRoles]) => ({
    customerId,
    roles: orgRoles.map(rawRoleName),
  }))
}

function statusDisplayName(status: string | undefined): Member['status'] {
  const normalized = (status || '').toLowerCase()
  if (normalized === 'disabled') return 'Disabled'
  if (normalized === 'invited') return 'Invited'
  return 'Active'
}

function accountTierFromApi(value: string | undefined): string {
  return (value || '').toLowerCase()
}

function mapInviteResult(result: {
  customerId?: string
  companyName?: string
  status?: InviteResult['status']
  exists?: boolean
  accounttier?: string
  accountTier?: string
  text?: string
}): InviteResult {
  return {
    customerId: result.customerId || '',
    companyName: result.companyName,
    status: result.status === 'Error' ? 'Error' : 'OK',
    exists: !!result.exists,
    accountTier: accountTierFromApi(result.accounttier || result.accountTier),
    text: result.text,
  }
}

function mapAssignResult(result: {
  customerId?: string
  companyName?: string
  status?: AssignOrganizationsResult['status']
  action?: AssignOrganizationsResult['action']
  loanOfficerId?: string
  accounttier?: string
  accountTier?: string
  text?: string
}): AssignOrganizationsResult {
  return {
    customerId: result.customerId || '',
    companyName: result.companyName,
    status: result.status === 'Error' ? 'Error' : 'OK',
    action: result.action,
    loanOfficerId: result.loanOfficerId,
    accountTier: accountTierFromApi(result.accounttier || result.accountTier),
    text: result.text,
  }
}

function organizationFromApi(org: ManagedOrganizationApiRecord, index: number): Organization {
  const roles = org.roles || []
  return {
    id: org.customerId,
    customerId: org.customerId,
    name: org.companyName || org.customerId,
    short: makeOrgShort(org.companyName || org.customerId),
    color: pickOrgColor(index),
    address: org.address || '',
    plan: normalizeOrganizationPlan(org.plan),
    paidSeats: typeof org.paidSeats === 'number' ? org.paidSeats : undefined,
    paidUsers: typeof org.paidUsers === 'number' ? org.paidUsers : undefined,
    memberCount: typeof org.memberCount === 'number' ? org.memberCount : undefined,
    isHardMoneyLoanCustomer: !!org.isHardMoneyLoanCustomer,
    myRole: roleDisplayName(roles),
    roles,
    canManageUsers: !!org.canManageUsers,
    canCreateOrganization: !!org.canCreateOrganization,
  }
}

export async function fetchManagedOrganizations(): Promise<ManagedOrganizationsResult> {
  const response = await fetch('/los/managed-organizations', {
    method: 'GET',
    headers: authHeaders(),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to load organizations')
  }
  const organizations = (data.organizations || []).map((org: ManagedOrganizationApiRecord, index: number) =>
    organizationFromApi(org, index),
  )
  return {
    canCreateOrganization: !!data.canCreateOrganization,
    organizations,
  }
}

export async function fetchOrganizationMembers(params: {
  customerId: string
  page: number
  pageSize: number
  search?: string
  filters?: MemberListFilters
}): Promise<MemberPage> {
  const query = new URLSearchParams({
    customerId: params.customerId,
    page: String(params.page),
    pageSize: String(params.pageSize),
  })
  const search = params.search?.trim()
  if (search) query.set('search', search)
  params.filters?.roles.forEach((role) => query.append('roles', rawRoleName(role)))
  params.filters?.organizationIds.forEach((organizationId) => query.append('organizations', organizationId))
  if (params.filters?.status) query.set('status', params.filters.status.toLowerCase())
  if (params.filters?.accountTier) query.set('accountTier', params.filters.accountTier.toLowerCase())

  const response = await fetch(`/los/managed-organization-loanofficers?${query.toString()}`, {
    method: 'GET',
    headers: {
      ...authHeaders(),
      'X-Image-Free': 'true',
    },
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to load users')
  }

  const members = (data.LoanOfficers || []).map((lo: LoanOfficerApiRecord): Member => {
    const roleList = roleDisplayNames(lo.Roles)
    return {
      id: lo.ID || '',
      email: lo.Email || '',
      firstName: lo.FirstName || '',
      lastName: lo.LastName || '',
      name: `${lo.FirstName || ''} ${lo.LastName || ''}`.trim() || lo.Email || '',
      nmls: lo.Nmls || '-',
      phone: lo.Phone || '',
      accountTier: accountTierFromApi(lo.AccountTier || lo.accounttier || lo.accountTier),
      status: statusDisplayName(lo.Status),
      roles: {
        [params.customerId]: roleDisplayName(lo.Roles),
      },
      roleLists: {
        [params.customerId]: roleList,
      },
      organizationIds: Array.from(new Set(
        (lo.OrganizationIds || []).map((id) => (id || '').trim()).filter(Boolean),
      )),
    }
  })

  return {
    members,
    total: data.Total || 0,
    page: data.Page || params.page,
    pageSize: data.PageSize || params.pageSize,
  }
}

export async function checkManagedOrganizationInvite(payload: InvitePayload): Promise<InviteResult[]> {
  const response = await fetch('/los/managed-organization-invite/check', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      email: payload.email,
      organizations: inviteOrganizationsFromRoles(payload.roles).map(({ customerId }) => ({ customerId })),
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to check invite')
  }
  return (data.results || []).map(mapInviteResult)
}

export async function sendManagedOrganizationInvite(payload: InvitePayload): Promise<InviteResult[]> {
  const response = await fetch('/los/managed-organization-invite', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      nmls: payload.nmls,
      organizations: inviteOrganizationsFromRoles(payload.roles),
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to send invite')
  }
  return (data.results || []).map(mapInviteResult)
}

// Resolves to the server's message: when an invited user's email changed it
// also says whether the invite went to the new address.
export async function updateManagedOrganizationLoanOfficer(payload: EditUserPayload): Promise<string> {
  const response = await fetch('/los/managed-organization-loanofficer/update', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      customerId: payload.customerId,
      id: payload.id,
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      nmls: payload.nmls,
      roles: payload.roles.map(rawRoleName),
      accounttier: payload.accountTier || '',
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to update user')
  }
  return data.Text || 'Account updated'
}

export async function resendManagedOrganizationInvite(payload: DisableUserPayload): Promise<void> {
  const response = await fetch('/los/managed-organization-loanofficer/resend-invite', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      customerId: payload.customerId,
      loanOfficerId: payload.loanOfficerId,
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to send the invitation')
  }
}

export async function disableManagedOrganizationLoanOfficer(payload: DisableUserPayload): Promise<void> {
  const response = await fetch('/los/managed-organization-loanofficer/disable', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      customerId: payload.customerId,
      loanOfficerId: payload.loanOfficerId,
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to disable user')
  }
}

export async function enableManagedOrganizationLoanOfficer(payload: DisableUserPayload): Promise<string> {
  const response = await fetch('/los/managed-organization-loanofficer/enable', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      customerId: payload.customerId,
      loanOfficerId: payload.loanOfficerId,
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to enable user')
  }
  return data.Text || 'Account enabled'
}

export async function fetchManagedOrganizationAssignments(params: {
  customerId: string
  loanOfficerId: string
}): Promise<AssignmentRecord[]> {
  const query = new URLSearchParams({
    customerId: params.customerId,
    loanOfficerId: params.loanOfficerId,
  })
  const response = await fetch(`/los/managed-organization-loanofficer/assignments?${query.toString()}`, {
    method: 'GET',
    headers: authHeaders(),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to load assignments')
  }
  return data.assignments || []
}

export async function assignManagedOrganizations(payload: AssignOrganizationsPayload): Promise<AssignOrganizationsResult[]> {
  const response = await fetch('/los/managed-organization-loanofficer/assign', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      sourceCustomerId: payload.sourceCustomerId,
      sourceLoanOfficerId: payload.sourceLoanOfficerId,
      organizations: inviteOrganizationsFromRoles(payload.roles),
    }),
  })
  const data = await response.json()
  if (!response.ok || data.Status !== 'OK') {
    throw new Error(data.Text || 'Failed to update assignments')
  }
  return (data.results || []).map(mapAssignResult)
}
